import { useQuery } from '@apollo/client';
import * as R from 'ramda';
import { ApolloProviderV4 } from '~/apollo/client-v4';
import {
  BookmarkParentType,
  BookmarkTargetType,
} from '~/apollo/generated/v3/graphql';
import { graphql } from '~/apollo/generated/v4';
import { PublishedContainer } from '~/components/common/published-container';
import { SpinnerPlaceholder } from '~/components/common/SpinnerPlaceholder';
import { GigaPanViewer } from '~/components/supporting-object/giga-pan-viewer';
import { PanoramaItemDisplayV4 } from '~/components/supporting-object/panorama-item-display-v4';
import { useRouteParam } from '~/hooks/routing';
import { outcropPanoramasTabRoute, studyPanoramasTabRoute } from '~/paths';
import { createRefetchQueries } from '~/utils/graphql';
import { isPublished } from '~/utils/modules/supporting-object';

const STUDY_PANORAMAS_ROUTE = graphql(`
  query StudyPanoramasRoute($id: ID!) {
    studyGet(id: $id) {
      id
      gigaPans {
        ...gigaPanParts
        pictures {
          ...pictureParts
          pictureUrl
          thumbnailUrl: pictureUrl(width: 500)
          file {
            ...fileParts
          }
        }
      }
    }
  }
`);

export default function StudyPanoramasPage() {
  return (
    <ApolloProviderV4>
      <StudyPanoramasPageInner />
    </ApolloProviderV4>
  );
}

function StudyPanoramasPageInner() {
  const studyId = useRouteParam('studyId', parseInt);

  const { data, loading } = useQuery(STUDY_PANORAMAS_ROUTE, {
    variables: { id: studyId },
  });

  const refetchQueries = createRefetchQueries([
    { query: STUDY_PANORAMAS_ROUTE, variables: { id: studyId } },
  ]);

  const study = data?.studyGet;

  const panoramas = R.pipe(
    () => study?.gigaPans ?? [],
    R.sortBy(gp => gp.priority ?? Infinity),
  )();

  const panoramaPictures = (panorama: (typeof panoramas)[number]) =>
    R.sortBy(p => p.priority ?? Infinity, panorama.pictures);

  const hasGigaPan = !!panoramas.find(p => !!p.gigaPanHash);

  if (loading) return <SpinnerPlaceholder />;

  return (
    <div className="space-y-4">
      {hasGigaPan && (
        <p>
          Note: Please be aware that the Gigapans load extremely slowly because
          they are embedded externally from the Gigapan website. A better
          hosting solution based on the Safari database is under discussion.
        </p>
      )}

      {panoramas.map(p => (
        <div key={p.id}>
          <PublishedContainer published={isPublished(p)}>
            <GigaPanViewer
              hash={p.gigaPanHash}
              bookmarkable={{
                parentType: BookmarkParentType.Study,
                parentId: studyId,
                targetType: BookmarkTargetType.GigaPan,
                targetId: parseInt(p.id),
                path: p.outcropTagId
                  ? outcropPanoramasTabRoute(p.outcropTagId)
                  : studyPanoramasTabRoute(studyId),
              }}
            />
            <PanoramaItemDisplayV4
              pictures={panoramaPictures(p)}
              bookmarkable={{
                parentType: BookmarkParentType.GigaPan,
                parentId: parseInt(p.id),
                path: p.outcropTagId
                  ? outcropPanoramasTabRoute(p.outcropTagId)
                  : studyPanoramasTabRoute(studyId),
              }}
              refetchQueries={refetchQueries}
            />
          </PublishedContainer>
        </div>
      ))}
    </div>
  );
}
